<template>
  <div class="seller-page">
    <div v-if="loading" class="loading">
      <div class="spinner"></div>
      Loading seller details...
    </div>

    <div v-if="loadingMorePages" class="loading-overlay">
      <div class="spinner"></div>
      Loading next page...
    </div>
    
    <div v-if="error" class="error">
      {{ error }}
      <button @click="fetchSellerData" class="retry-button">Try Again</button>
    </div>
    
    <div v-if="!loading && !error && seller" class="seller-container">
      <!-- Seller Info Header -->
      <div class="seller-header">
        <div class="seller-profile">
          <img 
            :src="seller.sellerImage" 
            :alt="seller.sellerName"
            @error="handleImageError"
            class="seller-avatar"
          />
          <div class="seller-details">
            <h1 class="seller-name">{{ seller.sellerName }}</h1>
            <p class="seller-stats">{{ seller.sellerInfo }}</p>
          </div>
        </div>
      </div>

      <!-- Seller Products -->
      <div class="seller-products">
        <h2>Products from this seller</h2>
        <div class="products">
          <div 
            v-for="product in recommendedProducts" 
            :key="product.productLink" 
            class="product-card"
            @click="goToProductDetail(product)"
          >
            <div class="product-image">
              <img :src="product.image" :alt="product.text" @error="handleProductImageError" loading="lazy" />
            </div>
            <div class="product-details">
              <h3 class="product-text-title">{{ product.text }}</h3>
              <div class="price-reviews-container">
                <div v-if="product.originalPrice" class="price-container">
                   <p class="product-price original-price" style="text-decoration: line-through; color: black; font-size: 0.9em; margin-right: 5px;">{{ product.originalPrice }}</p>
                   <p class="product-price new-price" style="color: red; font-weight: bold;">{{ product.price }}</p>
                </div>
                <p v-else class="product-price">{{ product.price }}</p>
                <button class="favourite-btn" @click.stop.prevent="toggleFavourite(product)">
                  <i
                    v-if="isFavourited(product)"
                    class="fas fa-heart favourite-heart is-active"
                    :class="{ popping: poppingFavouriteUrl === getFavouriteUrl(product) }"
                  ></i>
                  <i v-else class="far fa-heart favourite-heart"></i>
                </button>
                <!--<span class="reviews-count" v-if="product.sellerReviews">{{ product.sellerReviews }}</span>-->
              </div>
              <div class="seller-details">
                <div class="seller-info">
                  <img 
                    :src="product.sellerImage" 
                    :alt="product.sellerName"
                    @error="handleSellerImageError"
                    class="seller-image"
                    loading="lazy"
                  />
                  <span class="seller-name-col">{{ product.sellerName }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="pagination" v-if="maxPages > 1">
        <button 
          class="pagination-btn" 
          :disabled="currentPage === 1"
          @click="goToPage(1)"
        >
          First
        </button>
        
        <button 
          class="pagination-btn" 
          :disabled="currentPage === 1"
          @click="goToPage(currentPage - 1)"
        >
          Previous
        </button>
        
        <input 
          type="number" 
          v-model.number="pageInput" 
          min="1" 
          :max="maxPages" 
          class="page-input"
        />
        <button 
          class="pagination-btn go-btn"
          @click="goToPage(pageInput)"
        >
          Go
        </button>

        <button 
          class="pagination-btn" 
          :disabled="currentPage === maxPages"
          @click="goToPage(currentPage + 1)"
        >
          Next
        </button>

        <button 
          class="pagination-btn" 
          :disabled="currentPage === maxPages"
          @click="goToPage(maxPages)"
        >
          Last
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import tokenService from '../services/tokenService';
import favouritesService from '../services/favouritesService';
import productNavigation from '../services/productNavigation';

export default {
  name: 'Seller',
  data() {
    return {
      seller: null,
      recommendedProducts: [],
      loading: true,
      loadingMorePages: false,
      error: null,
      currentPage: 1,
      maxPages: 1,
      pageSize: 20,
      pageInput: 1,
      favouritesMap: {},
      poppingFavouriteUrl: null,
    }
  },
  created() {
    // Restore page from URL if exists
    if (this.$route.query.page) {
      const p = parseInt(this.$route.query.page);
      if (!isNaN(p) && p > 0) {
        this.currentPage = p;
        this.pageInput = p;
      }
    }

    // Load favourites (session-based). Non-blocking.
    this.loadFavourites();
    this.$root.$on('favourites-changed', this.loadFavourites);

    // Get the seller ID from URL
    const userId = this.$route.query.userId;
    if (userId) {
      this.fetchSellerData(userId);
    } else {
      this.error = 'No seller ID provided';
      this.loading = false;
    }
  },
  watch: {
    '$route.query.userId'(newUserId) {
      if (newUserId && newUserId !== this.sellerData?.base?.userId) {
        this.currentPage = 1;
        this.pageInput = 1;
        this.fetchSellerData(newUserId);
      }
    },
    '$route.query.page'(newPage) {
      const page = newPage ? parseInt(newPage) : 1;
      if (page !== this.currentPage && !this.loadingMorePages && !this.loading) {
        this.goToPage(page);
      }
    }
  },
  beforeDestroy() {
    this.$root.$off('favourites-changed', this.loadFavourites);
  },
  computed: {
    displayedPageNumbers() {
      const current = this.currentPage;
      const max = this.maxPages;
      const delta = 2;
      
      let range = [];
      
      range.push(1);
      
      for (let i = Math.max(2, current - delta); i <= Math.min(max - 1, current + delta); i++) {
        range.push(i);
      }
      
      // Always include last page
      if (max > 1) {
        range.push(max);
      }
      
      // Add ellipses where needed
      let withEllipsis = [];
      let l;
      
      for (let i of range) {
        if (l) {
          if (i - l === 2) {
            withEllipsis.push(l + 1);
          } else if (i - l !== 1) {
            withEllipsis.push('...');
          }
        }
        withEllipsis.push(i);
        l = i;
      }
      
      return withEllipsis;
    },
  },
  methods: {
    getFavouriteUrl(product) {
      return favouritesService.getProductUrlFromProduct(product);
    },

    isFavourited(product) {
      const url = this.getFavouriteUrl(product);
      return !!(url && this.favouritesMap && this.favouritesMap[url]);
    },

    async loadFavourites() {
      try {
        const list = await favouritesService.listFavourites();
        const nextMap = {};
        for (const fav of list) {
          if (fav && fav.url) nextMap[fav.url] = true;
        }
        this.favouritesMap = nextMap;
      } catch (err) {
        if (err && err.response && err.response.status === 401) return;
        console.warn('Failed to load favourites', err);
      }
    },

    async toggleFavourite(product) {
      const url = this.getFavouriteUrl(product);
      if (!url) {
        this.$root.$emit('show-toast', { message: 'Unable to favourite: missing product URL.', type: 'error' });
        return;
      }

      const currentlyFav = !!this.favouritesMap[url];
      try {
        this.poppingFavouriteUrl = currentlyFav ? null : url;
        if (currentlyFav) {
          await favouritesService.removeFavourite(url);
          this.$delete(this.favouritesMap, url);
        } else {
          await favouritesService.addFavourite(url);
          this.$set(this.favouritesMap, url, true);
        }
        setTimeout(() => {
          if (this.poppingFavouriteUrl === url) this.poppingFavouriteUrl = null;
        }, 220);
      } catch (err) {
        if (err && err.response && err.response.status === 401) {
          if (!currentlyFav) favouritesService.setPendingFavourite(url);
          this.$root.$emit('open-login-modal');
          return;
        }
        this.$root.$emit('show-toast', {
          message: err?.response?.data?.error || err?.message || 'Failed to update favourite.',
          type: 'error'
        });
      }
    },

    async getAuthToken() {
      // Use the token service to get a valid API token
      return await tokenService.getApiToken();
    },

    async fetchSellerData(userId) {
      this.loading = true;
      this.error = null;

      try {
        // Use the executeAuthenticatedRequest method to handle token management automatically
        const response = await tokenService.executeAuthenticatedRequest(async (token) => {
          return await axios.post(`/api/seller?userId=${userId}&page=${this.currentPage}&pageSize=${this.pageSize}`, {}, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
        });

        if (response.data.sellerData && response.data.sellerData.data) {
          this.seller = response.data.sellerData.data;
        }

        if (response.data.recommendedProducts && response.data.recommendedProducts.data) {
          this.recommendedProducts = response.data.recommendedProducts.data;
        }
        console.log("Fetched seller data:", response.data);
        console.log("Current Page:", this.currentPage, "Max Pages:", this.maxPages);
        this.currentPage = response.data.page;
        this.maxPages = response.data.totalPages;
      } catch (error) {
        console.error('Error fetching seller data:', error);
        this.error = 'Failed to load seller information. Please try again.';
      } finally {
        this.loading = false;
      }
    },

    handleImageError(e) {
      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAiIGhlaWdodD0iMzAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMTUiIGN5PSIxNSIgcj0iMTUiIGZpbGw9IiNmMGYwZjAiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjE0IiBmaWxsPSIjNjY2IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+UzwvdGV4dD48L3N2Zz4=';
    },

    handleProductImageError(e) {
      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDUwIiBoZWlnaHQ9IjQ1MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmMGYwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIyNCIgZmlsbD0iIzY2NiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg==';
    },

    handleSellerImageError(e) {
      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAiIGhlaWdodD0iMzAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMTUiIGN5PSIxNSIgcj0iMTUiIGZpbGw9IiNmMGYwZjAiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjE0IiBmaWxsPSIjNjY2IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+UzwvdGV4dD48L3N2Zz4=';
    },

    goToProductDetail(product) {
      // Always stays inside Curator Eden — never follow the Goofish source URL.
      productNavigation.openProduct(this.$router, product);
    },
    async goToPage(page) {
      if (page < 1 || page > this.maxPages || page === this.currentPage) {
        return;
      }

      this.loadingMorePages = true;
      this.currentPage = page;
      this.pageInput = page;

      // Update URL to reflect new page
      try {
        if (Number(this.$route.query.page) !== page) {
          this.$router.replace({
            query: { ...this.$route.query, page }
          }).catch(err => {
            if (err.name !== 'NavigationDuplicated') console.error(err);
          });
        }
      } catch (e) {
        // ignore
      }

      window.requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });

      try {
        const userId = this.$route.query.userId;
        if (!userId) {
          this.error = 'No seller ID provided';
          return;
        }

        const response = await tokenService.executeAuthenticatedRequest(async (token) => {
          return await this.$http.post(`/api/seller?userId=${userId}&page=${page}&pageSize=${this.pageSize}`, {}, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
        });

        if (response.data.sellerData && response.data.sellerData.data) {
          this.seller = response.data.sellerData.data;
        }

        if (response.data.recommendedProducts && response.data.recommendedProducts.data) {
          this.recommendedProducts = response.data.recommendedProducts.data;
        }

        this.currentPage = response.data.page;
        this.maxPages = response.data.totalPages;
        this.error = null;
      } catch (error) {
        console.error('Error fetching seller data on page change:', error);
        this.error = 'Failed to load seller information. Please try again.';
      } finally {
        this.loadingMorePages = false;
      }
    }
  }
}
</script>

<style scoped>

.page-input {
  width: 60px;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  text-align: center;
  font-size: 1rem;
}

.page-input:focus {
  outline: none;
  border-color: #3498db;
  box-shadow: 0 0 5px rgba(52, 152, 219, 0.5);
}
.pagination {
  margin-top: 15px;
}

.pagination-btn {
  padding: 8px 15px;
  font-size: 14px;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 10px;
}

.pagination-btn {
  padding: 6px 12px;
  font-size: 13px;
}

  .pagination {
    margin-top: 8px;
  }

  .pagination-btn {
    padding: 5px 10px;
    font-size: 12px;
  }
  .pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
  gap: 10px;
  flex-wrap: wrap;
  padding: 0 10px;
}
.pagination-btn {
  padding: 10px 20px;
  background: #000000;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}
.pagination-btn:disabled {
  background: #666666;
  cursor: not-allowed;
}

.pagination-btn:hover:not(:disabled) {
  background: #333333;
}
.pagination-btn.go-btn {
  background: #ffffff;
  color: #000000;
  border: 1px solid #000000;
}

.pagination-btn.go-btn:hover:not(:disabled) {
  background: #f0f0f0;
}

.pagination-btn.go-btn:disabled {
  background: #f0f0f0;
  color: #666666;
  border-color: #666666;
}

.seller-page {
  padding: 20px;
  
  max-width: 1200px;
  margin: 0 auto;
  /*
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  */
  }

.loading {
  text-align: center;
  padding: 40px;
  color: #666;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;
  background: rgba(255, 255, 255, 0.55);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #666;
  backdrop-filter: blur(2px);
}

.spinner {
  width: 40px;
  height: 40px;
  margin: 0 auto 20px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error {
  color: #721c24;
  background: #f8d7da;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 20px;
  text-align: center;
}

.retry-button {
  margin-top: 15px;
  padding: 10px 20px;
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s;
}

.retry-button:hover {
  background: #c82333;
}

.seller-container {
  background: white;
  /*
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  */
}

.seller-header {
  /*padding: 30px;*/
  /*border-bottom: 1px solid #eee;*/
}

.seller-profile {
  display: flex;
  align-items: center;
  gap: 20px;
}

.seller-avatar {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  object-fit: cover;
}

.seller-details {
  flex: 1;
}

.seller-name {
  margin: 0 0 10px;
  font-size: 24px;
  color: #333;
}

.seller-name-col {
  margin: 0 0 10px;
  font-size: 24px;
  color: #333;
}

.seller-stats {
  margin: 0;
  color: #666;
  font-size: 14px;
}

.seller-products {
  /*padding: 30px;*/
}

.seller-products h2 {
  margin: 0 0 20px;
  font-size: 20px;
  color: #333;
}

.products {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
} 

.product-card {
  overflow: hidden;
  background: white;
  animation: fadeIn 0.3s ease-in;
  /*display: flex;*/
  flex-direction: column;
  height: 350px; /*380px;*/
  position: relative;
  cursor: pointer;
}

.product-image {
  position: relative;
  width: 100%;
  height: 18rem;
  background: #f8f8f8;
}

.product-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.product-details {
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.product-text-title {
  margin: 0;
  font-size: 0.9rem;
  color: #333;
  font-weight: 500;
  line-height: 1.3;
  height: 2.8em;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
}

.price-reviews-container {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.price-reviews-container .favourite-btn {
  margin-left: auto;
}

.favourite-btn {
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  line-height: 1;
}

.favourite-heart {
  font-size: 18px;
}

.favourite-heart.is-active {
  color: #ff4444;
}

.favourite-heart.popping {
  animation: favPop 200ms ease-out;
}

@keyframes favPop {
  0% { transform: scale(0.85); }
  60% { transform: scale(1.15); }
  100% { transform: scale(1.0); }
}

.product-price {
  font-size: 1.1rem;
  color: #e44d26;
  font-weight: bold;
  margin: 0;
}

.reviews-count {
  font-size: 0.9rem;
  color: #888;
  margin: 0;
  align-content: center;
}

.seller-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.seller-image {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  margin-top: -13px;;
}

.seller-name {
  font-size: 0.8rem;
  color: #666;
  /*white-space: nowrap;
  flex: 1;*/
  overflow: hidden;
  text-overflow: ellipsis;
  
}
.seller-name-col {
  font-size: 0.8rem;
  color: #666;
  /*white-space: nowrap;
  flex: 1;*/
  overflow: hidden;
  text-overflow: ellipsis;
  
}
/*
@media (max-width: 1200px) {
  .products-grid {
    grid-template-columns: repeat(3, 1fr);
  }
  .product-card {
    height: 360px;
  }
}

@media (max-width: 900px) {
  .products-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .product-card {
    height: 340px;
  }
}

@media (max-width: 600px) {
  .products-grid {
    grid-template-columns: 1fr;
  }
  .product-card {
    height: 320px;
  }
  
  .seller-profile {
    flex-direction: column;
    text-align: center;
  }
  
  .seller-stats {
    text-align: center;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
  */


  @media (max-width: 768px) {
  .search {
    padding: 15px;
  }

  h1 {
    font-size: 1.2rem;
    margin-bottom: 15px;
  }

  .filters-container {
    flex-direction: column;
    align-items: stretch;
    gap: 15px;
    margin-bottom: 15px;
    padding: 15px;
    background: #f8f9fa;
    border-radius: 8px;
  }
  
  .sort-container {
    width: 100%;
    justify-content: stretch;
    margin-bottom: 0;
  }
  
  .sort-select {
    width: 100%;
    padding: 12px;
    font-size: 16px;
  }
  
  .price-filter {
    width: 100%;
  }
  
  .price-inputs {
    width: 100%;
    display: flex;
    flex-direction: row;
    gap: 10px;
  }
  
  .price-input-group {
    width: 50%;
    display: flex;
    align-items: center;
  }
  
  .price-input {
    width: 100%;
    padding: 12px;
    font-size: 16px;
  }
}

/* Remove all previous media queries for larger screens and start fresh */
@media (min-width: 760px) {
  .products {
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
  }
  .product-card {
    height: auto;
  }
  .product-image {
    height: 18rem;
  }
}

@media (max-width: 760px) {
  .filters-container {
    padding: 12px;
    gap: 12px;
  }

  .sort-container {
    margin-bottom: -8px;
  }

  .sort-select {
    font-size: 14px;
    padding: 10px;
  }

  .price-filter {
    gap: 8px;
  }

  .price-input-group label {
    font-size: 14px;
  }

  .price-input {
    font-size: 14px;
    padding: 10px;
  }

  .products {
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
    padding: 0 5px;
  }
  .product-card {
    height: auto;
  }
  .product-image {
    height: 11rem;
  }
  .product-details {
    padding: 8px;
  }
  .product-text-title {
    font-size: 0.85rem;
  }
}

@media (max-width: 480px) {
  .page-input {
    width: 40px;
    padding: 5px;
    font-size: 12px;
  }

  .pagination {
    gap: 4px;
    margin-top: 15px;
  }

  .pagination-btn {
    padding: 5px 10px;
    font-size: 12px;
    min-width: 45px;
  }

  .search {
    padding: 10px;
  }

  h1 {
    font-size: 1.1rem;
    padding: 0 10px;
  }

  .filters-container {
    padding: 12px;
    margin: 0 5px 15px 5px;
  }

  .sort-container {
    margin-bottom: -8px;
  }

  .price-inputs {
    gap: 8px;
  }

  .price-input-group {
    width: 50%;
  }

  .price-input {
    padding: 10px;
  }
}

@media (max-width: 470px) {
  .filters-container {
    padding: 10px;
    gap: 10px;
  }

  .sort-container {
    margin-bottom: -8px;
  }

  .sort-select {
    font-size: 13px;
    padding: 8px;
  }

  .price-filter {
    gap: 6px;
  }

  .price-inputs {
    gap: 8px;
  }

  .price-input-group label {
    display: none; /* Hide labels only at 380px and below */
  }

  .price-input-group {
    width: 50%;
  }

  .price-input {
    font-size: 13px;
    padding: 8px;
  }

  .products {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
    padding: 0 4px;
  }
  .product-card {
    height: auto;
  }
  .product-image {
    height: 11rem;
  }
  .product-details {
    padding: 8px;
  }
  .product-text-title {
    font-size: 0.85rem;
    height: 2.7em;
    /*height: 2.4em;*/
  }
  .product-price {
    font-size: 1rem;
  }
  .seller-details {
    padding-top: 6px;
  }
  .seller-image {
    width: 20px;
    height: 20px;
  }
  .seller-name {
    font-size: 0.8rem;
  }
  .seller-name-col {
    font-size: 0.8rem;
    white-space: nowrap;
  }
  .reviews-count {
    font-size: 0.75rem;
    margin-left: 28px;
  }
}

@media (max-width: 768px) {
  .pagination {
    flex-wrap: nowrap;
    gap: 4px;
  }

  .pagination-btn {
    padding: 6px 6px;
    font-size: 12px;
    white-space: nowrap;
    min-width: 0;
  }

  .page-input {
    width: 40px;
    padding: 6px 4px;
    font-size: 12px;
  }
}

@media (max-width: 380px) {
  .page-input {
    width: 35px;
    padding: 4px;
    font-size: 11px;
  }
  
  .pagination {
    gap: 3px;
  }

  .pagination-btn {
    padding: 4px 8px;
    font-size: 11px;
    min-width: 40px;
  }

  .filters-container {
    padding: 8px;
    gap: 8px;
  }

  .sort-container {
    margin-bottom: -8px;
  }

  .sort-select {
    font-size: 12px;
    padding: 8px;
  }

  .price-input-group label {
    display: none; /* Hide labels only at 380px and below */
  }

  .price-input {
    font-size: 12px;
    padding: 8px;
  }

  .product-card {
    height: auto;
  }
  .product-image {
    height: 11rem;
  }
  .product-details {
    padding: 6px;
  }
  .product-text-title {
    font-size: 0.8rem;
  }
}


</style> 
